import { Injectable, NgZone } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { IEntityLayerManager } from '../interfaces/entity-layer-manager.interface';
import { MapboxLayersService } from '../../mapbox-layers.service';
import { MapboxClusterService } from '../../mapbox-cluster.service';

/**
 * EntityManagerRegistry
 *
 * Registry central para todos los EntityLayerManagers.
 *
 * Responsabilidades:
 * - Registrar managers de entidades
 * - Inicializar todos los managers cuando el mapa esté listo
 * - Proporcionar acceso a managers específicos
 * - Limpiar todos los managers cuando sea necesario
 * - Coordinar operaciones batch (actualizar todos, limpiar todos, etc.)
 *
 * Patrón: Registry Pattern + Facade
 */
/** Margen (px) alrededor del click para encontrar entidades chicas. */
const TOLERANCIA_CLICK_PX = 8;

@Injectable({
  providedIn: 'root',
})
export class EntityManagerRegistry {
  // Mapa de managers registrados (entityType -> manager)
  private managers = new Map<string, IEntityLayerManager>();

  // Referencias a servicios
  private map: mapboxgl.Map | null = null;
  private layersService: MapboxLayersService | null = null;
  private clusterService: MapboxClusterService | null = null;
  private popupCallback:
    | ((datos: any, tipo: string, coordinate?: [number, number]) => void)
    | null = null;
  private ngZone: NgZone | null = null;

  // Estado
  private isInitialized = false;

  // Click handler global para evitar popups duplicados con markers superpuestos
  private globalClickHandlerRegistered = false;

  // Refs a los listeners globales para poder REMOVERLOS en el cambio de estilo.
  // setStyle() NO borra listeners del map (la instancia Map sobrevive), así que
  // sin estas refs se acumulaban N handlers 'click'/'mouseenter'/'mouseleave' por
  // cada switch de capa base → clicks duplicados y freeze progresivo.
  private globalClickFn: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
  private hoverMoveFn: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
  private hoverOutFn: (() => void) | null = null;

  constructor() {}

  // ========== REGISTRO DE MANAGERS ==========

  /**
   * Registra un nuevo manager de entidad
   *
   * @param manager Manager a registrar
   */
  register(manager: IEntityLayerManager): void {
    const type = manager.entityType;

    if (this.managers.has(type)) {
      // console.warn(
      //   `⚠️ Manager para ${type} ya está registrado, será reemplazado`,
      // );
    }

    this.managers.set(type, manager);
    // Si el registry ya está inicializado, inicializar este manager inmediatamente
    if (
      this.isInitialized &&
      this.map &&
      this.layersService &&
      this.clusterService
    ) {
      manager.initialize(
        this.map,
        this.layersService,
        this.clusterService,
        this.popupCallback || undefined,
        this.ngZone || undefined,
      );
    }
  }

  /**
   * Registra múltiples managers a la vez
   *
   * @param managers Array de managers a registrar
   */
  registerAll(managers: IEntityLayerManager[]): void {
    managers.forEach((manager) => this.register(manager));
  }

  /**
   * Desregistra un manager
   *
   * @param entityType Tipo de entidad a desregistrar
   */
  unregister(entityType: string): void {
    const manager = this.managers.get(entityType);
    if (manager) {
      manager.cleanup();
      this.managers.delete(entityType);
    }
  }

  // ========== INICIALIZACIÓN ==========

  /**
   * Inicializa todos los managers registrados
   *
   * IMPORTANTE: Llamar cuando el mapa esté listo (evento 'load')
   *
   * @param map Instancia del mapa Mapbox
   * @param layersService Servicio de layers
   * @param clusterService Servicio de clustering
   * @param popupCallback Callback para mostrar popups
   * @param ngZone NgZone para ejecutar event listeners fuera de Angular
   */
  initializeAll(
    map: mapboxgl.Map,
    layersService: MapboxLayersService,
    clusterService: MapboxClusterService,
    popupCallback?: (
      datos: any,
      tipo: string,
      coordinate?: [number, number],
    ) => void,
    ngZone?: NgZone,
  ): void {
    if (this.isInitialized) {
      console.warn('⚠️ EntityManagerRegistry ya está inicializado');
      return;
    }

    this.map = map;
    this.layersService = layersService;
    this.clusterService = clusterService;
    this.popupCallback = popupCallback || null;
    this.ngZone = ngZone || null;

    // Inicializar todos los managers
    this.managers.forEach((manager, type) => {
      try {
        manager.initialize(
          map,
          layersService,
          clusterService,
          this.popupCallback || undefined,
          this.ngZone || undefined,
        );
      } catch (error) {
        console.error(`❌ Error inicializando manager ${type}:`, error);
      }
    });

    this.isInitialized = true;

    // Registrar click handler global para evitar problemas con markers superpuestos
    this.setupGlobalClickHandler();
  }

  // ========== ACCESO A MANAGERS ==========

  /**
   * Obtiene un manager específico por tipo de entidad
   *
   * @param entityType Tipo de entidad
   * @returns Manager o undefined si no existe
   */
  get(entityType: string): IEntityLayerManager | undefined {
    return this.managers.get(entityType);
  }

  /**
   * Obtiene un manager con tipado fuerte
   *
   * @param entityType Tipo de entidad
   * @returns Manager tipado o undefined
   */
  getTyped<T, M extends IEntityLayerManager<T>>(
    entityType: string,
  ): M | undefined {
    return this.managers.get(entityType) as M | undefined;
  }

  /**
   * Verifica si existe un manager para un tipo de entidad
   *
   * @param entityType Tipo de entidad
   */
  has(entityType: string): boolean {
    return this.managers.has(entityType);
  }

  /**
   * Obtiene todos los managers registrados
   */
  getAll(): IEntityLayerManager[] {
    return Array.from(this.managers.values());
  }

  /**
   * Obtiene todos los tipos de entidades registrados
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.managers.keys());
  }

  // ========== OPERACIONES BATCH ==========

  /**
   * Actualiza los datos de múltiples entidades a la vez
   *
   * @param dataByType Mapa de entityType -> array de entidades
   */
  updateDataBatch(dataByType: Map<string, any[]>): void {
    dataByType.forEach((entities, type) => {
      const manager = this.managers.get(type);
      if (manager) {
        manager.updateData(entities);
      } else {
        console.warn(`⚠️ No existe manager para tipo: ${type}`);
      }
    });
  }

  /**
   * Limpia los datos de todos los managers (sin destruirlos)
   */
  clearAllData(): void {
    this.managers.forEach((manager, type) => {
      manager.clearData();
    });
  }

  /**
   * Resetea el estado de todos los managers SIN eliminarlos del registry
   *
   * IMPORTANTE: Usar esto al cambiar el estilo del mapa (cuando setStyle() elimina todos los sources/layers)
   * NO usa cleanup() porque no queremos remover sources/layers del mapa (ya fueron eliminados por setStyle())
   */
  resetAll(): void {
    // Solo resetear el flag isInitialized de cada manager
    // NO llamar a cleanup() porque el mapa ya fue limpiado por setStyle()
    // Remover listeners globales del mapa ANTES de soltar la ref al map
    // (setStyle borra sources/layers pero NO los listeners → si no, se acumulan)
    this.teardownGlobalClickHandler();

    this.managers.forEach((manager: any, type) => {
      try {
        // Desconectar listeners de nivel-mapa y markers DOM del manager
        // (render/zoomend/donut) ANTES de nulificar manager.map: setStyle no los
        // remueve y quedarían acumulados u huérfanos comiendo clicks.
        if (typeof manager.detachListeners === 'function') {
          manager.detachListeners();
        }
        // Resetear el estado interno del manager
        if (manager.isInitialized !== undefined) {
          manager.isInitialized = false;
        }
        // Limpiar referencias sin tocar el mapa
        manager.map = null;
        manager.layersService = null;
        manager.clusterService = null;
        manager.layerIds = [];
      } catch (error) {
        console.error(`❌ Error reseteando manager ${type}:`, error);
      }
    });

    // Resetear el registry pero mantener los managers registrados
    this.map = null;
    this.layersService = null;
    this.clusterService = null;
    this.ngZone = null;
    this.isInitialized = false;
    this.globalClickHandlerRegistered = false; // Resetear flag de click handler global
  }

  /**
   * Limpia y destruye todos los managers
   *
   * IMPORTANTE: Llamar en ngOnDestroy
   *
   * @param targetMap Si se pasa, solo limpia los managers que apuntan a ese mapa.
   *   Esto evita que un mapa secundario (ej: mini-mapa en un dialog) destruya los
   *   layers de un mapa de fondo cuando los managers siguen apuntando a él.
   */
  cleanupAll(targetMap?: mapboxgl.Map): void {
    this.managers.forEach((manager, type) => {
      try {
        const managerAny = manager as any;
        if (targetMap && managerAny.map !== targetMap) {
          // Este manager apunta a otro mapa (ej: mapa de fondo), no tocar
          return;
        }
        manager.cleanup();
      } catch (error) {
        console.error(`❌ Error limpiando manager ${type}:`, error);
      }
    });

    // Solo limpiar referencias del registry si limpiamos todos (sin filtro de mapa)
    if (!targetMap) {
      // Remover listeners globales antes de soltar la ref al map
      this.teardownGlobalClickHandler();
      this.managers.clear();
      this.map = null;
      this.layersService = null;
      this.clusterService = null;
      this.ngZone = null;
      this.isInitialized = false;
      this.globalClickHandlerRegistered = false;
    }
  }

  // ========== VISIBILIDAD BATCH ==========

  /**
   * Controla la visibilidad de múltiples tipos de entidades
   *
   * @param visibilityMap Mapa de entityType -> visible
   */
  setVisibilityBatch(visibilityMap: Map<string, boolean>): void {
    visibilityMap.forEach((visible, type) => {
      const manager = this.managers.get(type);
      if (manager) {
        manager.setVisibility(visible);
      }
    });
  }

  /**
   * Oculta todos los layers de todos los managers
   */
  hideAll(): void {
    this.managers.forEach((manager) => {
      manager.setVisibility(false);
    });
  }

  /**
   * Muestra todos los layers de todos los managers
   */
  showAll(): void {
    this.managers.forEach((manager) => {
      manager.setVisibility(true);
    });
  }

  // ========== CLUSTERING BATCH ==========

  /**
   * Controla el clustering de múltiples tipos de entidades
   *
   * @param clusteringMap Mapa de entityType -> enabled
   */
  setClusteringBatch(clusteringMap: Map<string, boolean>): void {
    clusteringMap.forEach((enabled, type) => {
      const manager = this.managers.get(type);
      if (manager && manager.supportsClustering) {
        manager.setClusteringEnabled(enabled);
      }
    });
  }

  // ========== UTILIDADES ==========

  /**
   * Obtiene información de estado del registry
   */
  getStatus(): {
    initialized: boolean;
    managersCount: number;
    registeredTypes: string[];
    managersWithClustering: string[];
  } {
    const managersWithClustering = Array.from(this.managers.values())
      .filter((m) => m.supportsClustering)
      .map((m) => m.entityType);

    return {
      initialized: this.isInitialized,
      managersCount: this.managers.size,
      registeredTypes: this.getRegisteredTypes(),
      managersWithClustering,
    };
  }

  /**
   * Imprime información de debug en consola
   */
  debugPrint(): void {
    const status = this.getStatus();
  }

  // ========== CLICK HANDLER GLOBAL ==========

  /**
   * Configura un único click handler global para el mapa
   *
   * IMPORTANTE: Esto evita el problema de markers superpuestos donde
   * cada layer dispara su propio evento de click, causando que se abran
   * múltiples popups y el último "gane" sobrescribiendo los anteriores.
   *
   * Solución:
   * - Un único listener en el mapa (no en cada layer)
   * - Consulta todas las features en el punto del click
   * - Toma solo la primera (la visualmente superior)
   * - Delega al manager correspondiente
   */
  private setupGlobalClickHandler(): void {
    if (!this.map || this.globalClickHandlerRegistered) {
      return;
    }

    // IMPORTANTE: Ejecutar event listener fuera de Angular Zone
    const runOutside = this.ngZone
      ? (fn: () => void) => this.ngZone!.runOutsideAngular(fn)
      : (fn: () => void) => fn();

    runOutside(() => {
      // Obtener todos los layer IDs de todos los managers (excluyendo clusters)
      const allLayerIds = this.getAllEntityLayerIds();

      // Listener global en el mapa (no en layers específicos)
      const clickFn = (e: mapboxgl.MapMouseEvent) => {
        if (!this.map || !this.popupCallback) return;

        // Actualizar lista de layers por si se agregaron nuevos managers
        // CRÍTICO: filtrar SOLO layers que existen en el estilo actual.
        // En Mapbox GL v3, si el array `layers` de queryRenderedFeatures incluye
        // aunque sea UN id inexistente, la query devuelve [] (sin throw ni warning),
        // anulando TODOS los clicks. Esto pasa con capas base personalizadas (estilo
        // raster "pelado" sin ciertas capas), rompiendo popups en todas las entidades.
        const currentLayerIds = this.getAllEntityLayerIds().filter((id) =>
          this.map!.getLayer(id),
        );

        if (currentLayerIds.length === 0) return;

        // Consultar las features en el punto del click y, si no hay ninguna,
        // en un margen alrededor. A zoom bajo varias entidades se dibujan como
        // puntos de 4 px de radio: sin margen había que acertarle al píxel y el
        // popup sólo abría al acercarse hasta ver el pin.
        let allFeatures = this.map!.queryRenderedFeatures(e.point, {
          layers: currentLayerIds,
        });
        if (allFeatures.length === 0) {
          const t = TOLERANCIA_CLICK_PX;
          allFeatures = this.map!.queryRenderedFeatures(
            [
              [e.point.x - t, e.point.y - t],
              [e.point.x + t, e.point.y + t],
            ],
            { layers: currentLayerIds },
          );
          // La más cercana al click primero
          allFeatures = allFeatures
            .filter((f) => f.geometry.type === 'Point')
            .map((f) => {
              const p = this.map!.project(
                (f.geometry as GeoJSON.Point).coordinates as [number, number],
              );
              return { f, d: Math.hypot(p.x - e.point.x, p.y - e.point.y) };
            })
            .sort((a, b) => a.d - b.d)
            .map(({ f }) => f);
        }

        if (allFeatures.length === 0) return;

        // CLAVE: Tomar solo la PRIMERA feature (la visualmente superior, o la
        // más cercana si se buscó con margen)
        const feature = allFeatures[0];
        const properties = feature.properties || {};

        // Determinar el tipo de entidad desde el layer ID
        // Formato esperado: "{entityType}-layer" o "{entityType}-icon-layer"
        const sourceLayer = feature.layer?.id || '';
        let entityType = '';

        // Buscar el manager que contiene este layer
        for (const [type, manager] of this.managers.entries()) {
          const managerAny = manager as any;
          if (
            managerAny.layerIds &&
            Array.isArray(managerAny.layerIds) &&
            managerAny.layerIds.includes(sourceLayer)
          ) {
            entityType = type;
            break;
          }
        }

        if (!entityType) {
          return;
        }

        // Obtener el manager correspondiente
        const manager = this.managers.get(entityType);
        if (!manager) {
          return;
        }

        // Obtener la entidad transformada para el popup
        const entityId = properties['id'];
        const managerAny = manager as any;

        if (!managerAny.getEntityForPopupById) {
          return;
        }

        const entityForPopup = managerAny.getEntityForPopupById(entityId);

        if (!entityForPopup) {
          return;
        }

        // Obtener coordenadas del feature (centro del marker)
        const geometry = feature.geometry as GeoJSON.Point;
        const coordinate: [number, number] = geometry.coordinates as [
          number,
          number,
        ];

        // CRÍTICO: Volver a entrar en Angular Zone para actualizar UI del popup
        if (this.ngZone) {
          this.ngZone.run(() => {
            this.popupCallback!(entityForPopup, entityType, coordinate);
          });
        } else {
          this.popupCallback(entityForPopup, entityType, coordinate);
        }
      };
      this.map!.on('click', clickFn);
      this.globalClickFn = clickFn;

      // Cursor pointer al pasar sobre cualquier entity layer.
      // Un solo 'mousemove' con UNA consulta: antes era un par
      // mouseenter/mouseleave por layer (~48 en total, de todos los managers),
      // y Mapbox resuelve cada uno con su propio queryRenderedFeatures en cada
      // movimiento del mouse (3-8 ms por evento, también durante el arrastre).
      const hoverLayerIds = allLayerIds.filter((id) => this.map!.getLayer(id));
      const moveFn = (e: mapboxgl.MapMouseEvent) => {
        if (!this.map || this.map.isMoving()) return;
        const hay =
          hoverLayerIds.length > 0 &&
          this.map.queryRenderedFeatures(e.point, { layers: hoverLayerIds })
            .length > 0;
        this.map.getCanvas().style.cursor = hay ? 'pointer' : '';
      };
      const outFn = () => {
        if (this.map) this.map.getCanvas().style.cursor = '';
      };
      this.map!.on('mousemove', moveFn);
      this.map!.on('mouseout', outFn);
      this.hoverMoveFn = moveFn;
      this.hoverOutFn = outFn;
    });

    this.globalClickHandlerRegistered = true;
  }

  /**
   * Remueve los listeners globales (click + hover) del mapa.
   *
   * CRÍTICO llamarlo en el cambio de estilo: setStyle() reconstruye sources/layers
   * pero NO toca los listeners del map (la instancia Map sobrevive). Sin este
   * teardown se acumulaba un handler nuevo por cada switch de capa base.
   */
  private teardownGlobalClickHandler(): void {
    if (this.map) {
      if (this.globalClickFn) {
        this.map.off('click', this.globalClickFn);
      }
      if (this.hoverMoveFn) this.map.off('mousemove', this.hoverMoveFn);
      if (this.hoverOutFn) this.map.off('mouseout', this.hoverOutFn);
    }
    this.globalClickFn = null;
    this.hoverMoveFn = null;
    this.hoverOutFn = null;
    this.globalClickHandlerRegistered = false;
  }

  /**
   * Obtener todos los layer IDs de todos los managers (excluyendo clusters)
   * PÚBLICO para que pueda ser usado externamente si es necesario
   */
  public getAllEntityLayerIds(): string[] {
    const allLayerIds: string[] = [];
    this.managers.forEach((manager: any) => {
      if (manager.layerIds && Array.isArray(manager.layerIds)) {
        const entityLayerIds = manager.layerIds.filter(
          (id: string) =>
            // Excluir clusters
            !id.includes('-cluster-layer') &&
            !id.includes('-cluster-count-layer') &&
            // Excluir círculos de cobertura (solo visuales, no interactivos).
            // Ojo: `-circle-layer` (singular) NO es cobertura: son los puntos
            // con que luminarias y sirenas se dibujan a zoom bajo, y los de
            // alertas/eventos. Excluirlos dejaba sin popup hasta ver el pin.
            !id.includes('-circles-layer') &&
            !id.endsWith('-stroke'),
        );
        allLayerIds.push(...entityLayerIds);
      }
    });
    return allLayerIds;
  }
}
